import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { statCardCircleSx, statCardSx } from '../../departmentIncomeTracker/components/DepartmentStatCard';
import { useElementSize } from '../../departmentIncomeTracker/utils/layoutHooks';
import { croCount, inr, pct, tarBand } from '../utils/trackerConfig';
import { fitFontSize, useFontsReady } from '../utils/textFit';

// The same card as the Accounts Manager's department cards (blue, round highlight, white text, a coloured dot beside the name). The dot is the
// only colour that tells the tiles apart: Total = light blue, Sales = green, Fee = amber (the same colours as those columns in the grid),
// TAR = its green / amber / red band, No of Sales = violet.
const DOT = { total: '#93C5FD', sales: '#34D399', fee: '#FBBF24', count: '#C4B5FD' };
const BAND_DOT = { '100%+ on target': '#34D399', '75–99%': '#FBBF24', 'Below 75%': '#F87171' };

const VALUE_MAX_PX = 18.4; // 1.15rem
const VALUE_MIN_PX = 12;

// The amount is never cut: it is shown in full, in the biggest type that fits this card (measured with the page's real font). Only if even the
// smallest size does not fit may it wrap onto a second line - the last digits are never hidden. The label and the caption wrap too.
function Card({ dot, label, value, caption, onClick }) {
  const [boxRef, , boxW] = useElementSize();
  useFontsReady();
  const innerPx = boxW === null ? null : boxW - 20; // the card's own padding
  const valuePx = innerPx === null ? VALUE_MAX_PX : fitFontSize(value, innerPx, VALUE_MAX_PX, VALUE_MIN_PX, true);
  return (
    <Box
      ref={boxRef}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={onClick ? 'Click to see the rows behind this number' : undefined}
      sx={{
        ...statCardSx,
        flex: '1 1 130px',
        minWidth: 0,
        height: 'auto',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform .12s, box-shadow .12s',
        '&:hover': onClick ? { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.22)' } : {},
        '&:focus-visible': { outline: '3px solid #fff', outlineOffset: -3 },
      }}
    >
      <Box sx={statCardCircleSx} />
      <Box sx={{ position: 'relative', zIndex: 1, p: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, flexShrink: 0, borderRadius: '50%', backgroundColor: dot, border: '2px solid rgba(255,255,255,0.9)' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            {label}
          </Typography>
        </Box>
        <Typography title={value} sx={{ fontSize: `${valuePx}px`, fontWeight: 700, lineHeight: 1.3, letterSpacing: '0.2px', fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, lineHeight: 1.3 }}>
          {caption}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Four headline cards from the project totals the page already has (so they follow the same
 * scope / employee filter as the tables): Total Revenue, Sales Revenue, Fee Revenue, and TAR
 * (or the number of sales when there is no target - e.g. in a week view).
 */
export default function KpiCards({ projects, showTar, onDrill }) {
  const t = useMemo(() => {
    const sum = (k) => projects.reduce((s, p) => s + (Number(p.total[k]) || 0), 0);
    const total = sum('totalRevenue');
    const target = sum('target');
    return {
      total,
      sales: sum('salesRevenue'),
      fee: sum('feeRevenue'),
      target,
      noOfSales: sum('noOfSales'),
      cros: projects.reduce((s, p) => s + croCount(p.cros), 0),
      tar: target > 0 ? (total / target) * 100 : null,
    };
  }, [projects]);

  const share = (v) => (t.total > 0 ? `${((v / t.total) * 100).toFixed(0)}% of total` : '—');

  // a card opens the rows behind its number (nothing to open when it is zero)
  const open = (value, request) => (onDrill && Number(value) ? () => onDrill(request) : undefined);
  const band = tarBand(t.tar);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Card
        dot={DOT.total}
        label="Total Revenue"
        value={`₹ ${inr(t.total)}`}
        caption={`${t.cros} ${t.cros === 1 ? 'CRO' : 'CROs'}`}
        onClick={open(t.total, { metric: 'totalRevenue', title: 'All projects — Total Revenue', scope: {} })}
      />
      <Card
        dot={DOT.sales}
        label="Sales Revenue"
        value={`₹ ${inr(t.sales)}`}
        caption={share(t.sales)}
        onClick={open(t.sales, { metric: 'salesRevenue', title: 'All projects — Sales Revenue', scope: {} })}
      />
      <Card
        dot={DOT.fee}
        label="Fee Revenue"
        value={`₹ ${inr(t.fee)}`}
        caption={share(t.fee)}
        onClick={open(t.fee, { metric: 'feeRevenue', title: 'All projects — Fee Revenue', scope: {} })}
      />
      {showTar && t.tar !== null ? (
        <Card
          dot={(band && BAND_DOT[band.label]) || DOT.total}
          label="Overall TAR"
          value={pct(t.tar)}
          caption={`Target ₹ ${inr(t.target)}`}
          onClick={open(1, { metric: 'tar', title: 'All projects — TAR', formula: { kind: 'tar', revenue: t.total, target: t.target, tar: t.tar } })}
        />
      ) : (
        <Card
          dot={DOT.count}
          label="No of Sales"
          value={inr(t.noOfSales)}
          caption="Students sold in this period"
          onClick={open(t.noOfSales, { metric: 'sales', title: 'All projects — No of Sales', scope: {} })}
        />
      )}
    </Box>
  );
}
